import { useState, useEffect, useCallback } from "react";
import { formatPlAmount } from "../utils/formatPlAmount";
import {
  getRevenueBootstrapData,
  getRevenueBootstrapError,
  getRevenueBootstrapPromise,
} from "../state/revenueBootstrapStore";
import { useRevenueBootstrap } from "./useRevenueBootstrap";
import type { KeyMetricsRevenueEntity } from "../../domain/entities/KeyMetricsRevenueEntity";

function applyKeyMetricsEntity(entity: KeyMetricsRevenueEntity): {
  ytdRevenue: string;
  ytdSubtext: string;
  collectionRateValue: string;
  collectionRateBadge: string;
  dsoAvgDays: number | null;
  dsoChangeText: string | undefined;
  dsoChangeVariant: "up" | "down" | undefined;
  dsoWindowLabel: string;
  dsoStatusLabel: string;
  dsoStatus: "excellent" | "healthy" | "watch" | "critical";
} {
  let dsoChangeText: string | undefined;
  let dsoChangeVariant: "up" | "down" | undefined;
  if (entity.dsoChangeDays === 0) {
    dsoChangeText = "No change";
    dsoChangeVariant = undefined;
  } else if (entity.dsoChangeVariant) {
    const absDays = Math.abs(entity.dsoChangeDays ?? 0);
    const arrow = entity.dsoChangeVariant === "up" ? "↑" : "↓";
    const dayLabel = absDays === 1 ? "day" : "days";
    dsoChangeText = `${arrow} ${absDays} ${dayLabel}`;
    dsoChangeVariant = entity.dsoChangeVariant;
  } else {
    dsoChangeText = undefined;
    dsoChangeVariant = undefined;
  }

  return {
    ytdRevenue: formatPlAmount(entity.ytdRevenue),
    ytdSubtext: entity.ytdSubtext,
    collectionRateValue:
      entity.collectionRateValue != null
        ? `${entity.collectionRateValue}%`
        : "—",
    collectionRateBadge: entity.collectionRateBadge,
    dsoAvgDays: entity.dsoAvgDays != null ? entity.dsoAvgDays : null,
    dsoChangeText,
    dsoChangeVariant,
    dsoWindowLabel: entity.dsoWindowLabel ?? "Latest closed month",
    dsoStatusLabel: entity.dsoStatusLabel,
    dsoStatus: entity.dsoStatus,
  };
}

export function useKeyMetricsRevenue() {
  useRevenueBootstrap();

  const [ytdRevenue, setYtdRevenue] = useState<string>("—");
  const [ytdSubtext, setYtdSubtext] = useState<string>("");
  const [collectionRateValue, setCollectionRateValue] = useState<string>("—");
  const [collectionRateBadge, setCollectionRateBadge] =
    useState<string>("Stable");
  const [dsoAvgDays, setDsoAvgDays] = useState<number | null>(null);
  const [dsoChangeText, setDsoChangeText] = useState<string | undefined>(
    undefined,
  );
  const [dsoChangeVariant, setDsoChangeVariant] = useState<
    "up" | "down" | undefined
  >(undefined);
  const [dsoStatusLabel, setDsoStatusLabel] = useState<string>("Healthy");
  const [dsoWindowLabel, setDsoWindowLabel] = useState<string>(
    "Latest closed month",
  );
  const [dsoStatus, setDsoStatus] = useState<
    "excellent" | "healthy" | "watch" | "critical"
  >("healthy");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const syncFromBootstrap = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let bootstrapEntity = getRevenueBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getRevenueBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getRevenueBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(bootstrapError ?? "Revenue bootstrap data unavailable");
      }

      const section = bootstrapEntity.sections.keyMetrics;
      if (section.status !== "ok" || !section.data) {
        throw new Error(section.error ?? "Key metrics unavailable");
      }

      const vm = applyKeyMetricsEntity(section.data);
      setYtdRevenue(vm.ytdRevenue);
      setYtdSubtext(vm.ytdSubtext);
      setCollectionRateValue(vm.collectionRateValue);
      setCollectionRateBadge(vm.collectionRateBadge);
      setDsoAvgDays(vm.dsoAvgDays);
      setDsoChangeText(vm.dsoChangeText);
      setDsoChangeVariant(vm.dsoChangeVariant);
      setDsoWindowLabel(vm.dsoWindowLabel);
      setDsoStatus(vm.dsoStatus);
      setDsoStatusLabel(vm.dsoStatusLabel);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load key metrics";
      setError(message);
      setYtdRevenue("—");
      setYtdSubtext("");
      setCollectionRateValue("—");
      setDsoAvgDays(null);
      setDsoChangeText(undefined);
      setDsoChangeVariant(undefined);
      setDsoWindowLabel("Latest closed month");
      setDsoStatus("healthy");
      setDsoStatusLabel("Healthy");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void syncFromBootstrap();
  }, [syncFromBootstrap]);

  return {
    ytdRevenue,
    ytdSubtext,
    collectionRateValue,
    collectionRateBadge,
    dsoAvgDays,
    dsoChangeText,
    dsoChangeVariant,
    dsoWindowLabel,
    dsoStatus,
    dsoStatusLabel,
    loading,
    error,
    refetch: syncFromBootstrap,
  };
}
